/**
 * Content manifest — single source of truth for dataroom pages.
 *
 * Each page is a pure React/TSX component living alongside this manifest
 * in ./pages. The nav sidebar (RoomLayout) and the router (main.tsx) both
 * consume the exported sections / pages.
 *
 * Adding a page: write ./pages/Foo.tsx, import it below, add an entry to
 * the right section. No build pipeline, no markdown compilation, no
 * plugin soup — just React.
 */

import type { ComponentType } from "react";

import { Home } from "./pages/Home";
import { Opportunity } from "./pages/Opportunity";
import { Team } from "./pages/Team";
import { Product } from "./pages/Product";
import { Technology } from "./pages/Technology";
import { Traction } from "./pages/Traction";
import { Business } from "./pages/Business";
import { Financials } from "./pages/Financials";
import { CapTable } from "./pages/CapTable";
import { Governance } from "./pages/Governance";
import { Legal } from "./pages/Legal";
import { Faq } from "./pages/Faq";

export interface PageDef {
  slug: string;
  title: string;
  Component: ComponentType;
}

export interface SectionDef {
  label?: string;
  pages: PageDef[];
}

export const sections: SectionDef[] = [
  {
    pages: [{ slug: "", title: "Overview", Component: Home }],
  },
  {
    label: "The Company",
    pages: [
      { slug: "opportunity", title: "The Opportunity", Component: Opportunity },
      { slug: "team", title: "The Team", Component: Team },
      { slug: "product", title: "The Product", Component: Product },
      { slug: "technology", title: "Technology", Component: Technology },
    ],
  },
  {
    label: "The Business",
    pages: [
      { slug: "traction", title: "Traction", Component: Traction },
      { slug: "business", title: "The Business", Component: Business },
      { slug: "financials", title: "Financials", Component: Financials },
    ],
  },
  {
    label: "The Deal",
    pages: [
      { slug: "cap-table", title: "Cap Table", Component: CapTable },
      { slug: "governance", title: "Governance", Component: Governance },
      { slug: "legal", title: "Legal", Component: Legal },
    ],
  },
  {
    pages: [{ slug: "faq", title: "FAQ", Component: Faq }],
  },
];

/** Flat list for routing. */
export const pages: PageDef[] = sections.flatMap((s) => s.pages);
